import logError from 'Helpers/logError';
import Resource from './Resource';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

class Provisioning extends Resource {
	_cache = {};
	constructor() {
		super('provisioning', null);
		this.provisioningNoAuth = new Resource('provisioningNoAuth');

		this.notifications = {
			buyAddonError: () => ({
				title: 'default.error',
				body: 'admin.billing.addon.failedToBuyAddon'
			})
		};
	}

	doSomething(method, parameters = {}, reqMethod = 'get', opts = {}) {
		if (Tools.ENV === 'ALPHA' || window.location.origin.includes('sandbox')) {
			switch (method) {
				case 'getAvailableAddons':
				case 'getSubscriptionAddons':
				case 'getTrialAddons':
					return Promise.resolve({ data: [] });
				default:
					return Promise.resolve({ data: {} });
			}
		}
		var promise;
		if (reqMethod === 'post') {
			promise = super._postRequest(`?method=${method}`, { method, ...parameters }, opts);
		} else {
			let params = '';
			Object.keys(parameters).forEach(key => {
				if (parameters[key]) {
					params += '&' + key + '=' + parameters[key];
				}
			});
			promise = super._getRequest(`?method=${method}${params}`, { methodName: method, ...opts });
		}

		return promise
			.then(res => ({
				error: null,
				data: res.data
			}))
			.catch(e => {
				throw { error: e, data: null };
			});
	}

	// NOTE: getPotentialAddonCost is not cached here because it takes dynamic parameters
	// (type, mailCampaignId, scheduledSendDate, etc.). Could be cached with a param-based key
	// if it shows up as a hot path.
	_cachedRequest(key, fn) {
		const cached = this._cache[key];
		if (cached && Date.now() - cached.time < CACHE_TTL) {
			return Promise.resolve(cached.data);
		}
		if (!cached?.promise) {
			const promise = fn()
				.then(data => {
					this._cache[key] = { data, time: Date.now(), promise: null };
					return data;
				})
				.catch(err => {
					delete this._cache[key];
					throw err;
				});
			this._cache[key] = { ...this._cache[key], promise };
		}
		return this._cache[key].promise;
	}

	clearCache() {
		this._cache = {};
		localStorage.removeItem('trialCache');
	}

	/* PROVISIONING */

	hasApp(alias) {
		return this._cachedRequest(`hasProduct:${alias}`, () =>
			this.doSomething('hasProduct', { type: 'integration', productAlias: alias })
		);
	}

	/* BILLING */

	getSubscription() {
		return this._cachedRequest('getSubscription', () => this.doSomething('getSubscription'));
	}

	getFutureSubscription() {
		return this._cachedRequest('getFutureSubscription', () => this.doSomething('getFutureSubscription'));
	}
	getActiveLicense() {
		return this._cachedRequest('getActiveLicense', () => this.doSomething('getActiveLicense'));
	}

	getFutureLicense() {
		return this._cachedRequest('getFutureLicense', () => this.doSomething('getFutureLicense'));
	}

	getSubscriptionAddons() {
		return this._cachedRequest('getSubscriptionAddons', () => this.doSomething('getSubscriptionAddons'));
	}

	getSubscriptionAgents() {
		return this._cachedRequest('getSubscriptionAgents', () => this.doSomething('getSubscriptionAgents')).catch(
			err => {
				logError(err, 'Failed to getSubscriptionAgents');
				return { data: [] };
			}
		);
	}

	getTrialAddons(clientId) {
		// We need to pass clientId to this function since we try to get trial addons right after we've logged in, but before AppService is initialized
		// I can't use the functions in LocalStorage.ts here since it uses AppService

		let trialCache = JSON.parse(localStorage.getItem('trialCache')) || {};
		// Once we're done with the login process we can use AppService to get the customerId
		const customerId = Tools.AppService.getCustomerId() || clientId || 0;
		if (customerId) {
			try {
				if (trialCache[customerId] && trialCache[customerId]?.expiry > new Date().getTime()) {
					return Promise.resolve(trialCache[customerId]);
				} else {
					// We fully reset the cache if the expiry date has passed/the customerId has changed in order to clear out potentially other customerIds from the cache
					trialCache = {};
					localStorage.removeItem('trialCache');

					const res = this.doSomething('getTrialAddons');
					return res
						.then(data => {
							if (!data.error) {
								trialCache[customerId] = data;
								trialCache[customerId].expiry = new Date().getTime() + 1000 * 60 * 60 * 1; // 1 hour, feel free to change if you want :)
								localStorage.setItem('trialCache', JSON.stringify(trialCache));
								return data;
							} else {
								return data;
							}
						})
						.catch(e => {
							trialCache = {};
							localStorage.removeItem('trialCache');
							logError('error in getTrialAddons:', e);
						});
				}
			} catch (e) {
				trialCache = {};
				localStorage.removeItem('trialCache');
				logError('error in getTrialAddons:', e);
			}
		}
		return this.doSomething('getTrialAddons');
	}

	getCustomerDetails() {
		return this._cachedRequest('getCustomerDetails', () => this.doSomething('getCustomerDetails'));
	}

	getLicenceInfo() {
		return this._cachedRequest('getLicenceInfo', () => this.doSomething('getLicenceInfo'));
	}

	getProduct(productAlias, type) {
		const options = { productAlias };
		if (type) {
			options.type = type;
		}
		return this._cachedRequest(`getProduct:${productAlias}:${type}`, () => this.doSomething('getProduct', options));
	}

	purchase(productAlias) {
		return this.doSomething('purchase', { productAlias }).then(res => {
			this.clearCache();
			return res;
		});
	}

	cancel(productAlias) {
		return this.doSomething('cancel', { productAlias }).then(res => {
			this.clearCache();
			return res;
		});
	}

	getSeatChangeInfo(seatDiff) {
		return this._cachedRequest(`getSeatChangeInfo:${seatDiff}`, () =>
			this.doSomething('getSeatChangeInfo', { seatDiff })
		);
	}

	addSeats(params) {
		return this.doSomething('addSeats', params).then(res => {
			this.clearCache();
			return res;
		});
	}
	removeSeats(params, opts = {}) {
		return this.doSomething('removeSeats', params, 'get', opts).then(res => {
			this.clearCache();
			return res;
		});
	}

	getSupportLicenceInfo() {
		return this._cachedRequest('getSupportLicenceInfo', () => this.doSomething('getSupportLicenceInfo'));
	}

	getSupportSeatChangeInfo(seatDiff) {
		return this._cachedRequest(`getSupportSeatChangeInfo:${seatDiff}`, () =>
			this.doSomething('getSupportSeatChangeInfo', { seatDiff })
		);
	}

	addSupportSeats(params) {
		return this.doSomething('addSupportSeats', params).then(res => {
			this.clearCache();
			return res;
		});
	}

	removeSupportSeats(params, opts = {}) {
		return this.doSomething('removeSupportSeats', params, 'get', opts).then(res => {
			this.clearCache();
			return res;
		});
	}

	getAddonSeatInfo(alias) {
		return this._cachedRequest(`getAddonSeatInfo:${alias}`, () => this.doSomething('getAddonSeatInfo', { alias }));
	}

	getAddonSeatChangeInfo(alias, seatDiff) {
		return this._cachedRequest(`getAddonSeatChangeInfo:${alias}:${seatDiff}`, () =>
			this.doSomething('getAddonSeatChangeInfo', { alias, seatDiff })
		);
	}

	addAddonSeats(params) {
		return this.doSomething('addAddonSeats', params).then(res => {
			this.clearCache();
			return res;
		});
	}

	removeAddonSeats(params, opts = {}) {
		return this.doSomething('removeAddonSeats', params, 'get', opts).then(res => {
			this.clearCache();
			return res;
		});
	}

	addBisnodeCredits(params) {
		return this.doSomething('addBisnodeCredits', params).then(res => {
			this.clearCache();
			return res;
		});
	}

	saveBillingAddress(address) {
		return this.doSomething('saveBillingAddress', address).then(res => {
			this.clearCache();
			return res;
		});
	}

	getAvailableAddons({ useCache = true } = {}) {
		if (!useCache) {
			delete this._cache['getAvailableAddons'];
		}
		return this._cachedRequest('getAvailableAddons', () => this.doSomething('getAvailableAddons'));
	}

	getAvailableAgents({ useCache = true } = {}) {
		if (!useCache) {
			delete this._cache['getAvailableAgents'];
		}
		return this._cachedRequest('getAvailableAgents', () => this.doSomething('getAvailableAgents')).catch(err => {
			logError(err, 'Failed to getAvailableAgents');
			return { data: [] };
		});
	}

	buyAddon(alias, quantity) {
		const params = { alias };
		if (quantity) {
			params.quantity = quantity;
		}
		return this.doSomething('buyAddon', params).then(res => {
			this.clearCache();
			return res;
		});
	}

	buyAgent(alias) {
		return this.doSomething('buyAgent', { alias }).then(res => {
			this.clearCache();
			return res;
		});
	}

	addTrialAddon(alias, productId, trialPeriod) {
		return this.doSomething('addTrialAddon', { alias, productId, trialPeriod }).then(res => {
			this.clearCache();
			return res;
		});
	}

	cancelAddon(productId, opts = {}) {
		return this.doSomething('cancelAddon', { productId }, 'get', opts).then(res => {
			this.clearCache();
			return res;
		});
	}

	cancelAgent(productId, opts = {}) {
		return this.doSomething('cancelAgent', { productId }, 'get', opts).then(res => {
			this.clearCache();
			return res;
		});
	}

	cancelSubscription({ agreementGroup, reasons }, opts = {}) {
		return this.doSomething('cancelSubscription', { agreementGroup, reasons }, 'post', opts).then(res => {
			this.clearCache();
			return res;
		});
	}

	reactivateAddon(productId) {
		return this.doSomething('reactivateAddon', { productId }).then(res => {
			this.clearCache();
			return res;
		});
	}

	reactivateAgent(productId) {
		return this.doSomething('reactivateAgent', { productId }).then(res => {
			this.clearCache();
			return res;
		});
	}

	reactivateSubscription() {
		return this.doSomething('reactivateSubscription').then(res => {
			this.clearCache();
			return res;
		});
	}
	makeGroup(agreementGroup) {
		return this.doSomething('makeGroup', { agreementGroup }).then(res => {
			this.clearCache();
			return res;
		});
	}
}

const resource = new Provisioning();
export default resource;
